import { FlatTreeControl } from '@angular/cdk/tree';
import { Injectable } from '@angular/core';
import { ProjectFileSystemType } from '@common/project-files/enums/project-file-system-type.enum';
import { ProjectFileUpdateAction } from '@portal-core/project-files/enums/project-file-update-action.enum';
import { ProjectFileManualUpdateAction } from '@portal-core/project-files/models/project-file-manual-update-action.model';
import { ProjectFile } from '@portal-core/project-files/models/project-file.model';
import { ProjectFilesTreeDataProvider } from '@portal-core/project-files/services/project-files-tree-data-provider';
import { ProjectFileFlatNode } from '@portal-core/project-files/util/project-file-flat-node';
import { ProjectFileNode } from '@portal-core/project-files/util/project-file-node';
import { BehaviorSubject, catchError, map, Observable, of, Subject } from 'rxjs';
import { ProjectFilesChecklistService } from './project-files-checklist.service';

@Injectable()
/**
 *  Only meant to be used by ProjectFilesTreeComponent as a provider.
 *  This service is used to create/manipulate normal Nodes for the tree used in ProjectFilesTreeComponent.
**/
export class ProjectFilesTreeDataService {

  projectId: number;
  includeFiles: boolean;
  fileFilter: string;
  committedFilter: boolean = null;
  dataProvider: ProjectFilesTreeDataProvider;
  dataChange: BehaviorSubject<ProjectFileNode[]> = new BehaviorSubject<[]>([]);
  manuallyEditedTreeData: BehaviorSubject<ProjectFileManualUpdateAction> = new BehaviorSubject<ProjectFileManualUpdateAction>(null);
  nodeMap: Map<string, ProjectFileNode> = new Map<string, ProjectFileNode>();
  retry$: Subject<void> = new Subject();
  treeControl: FlatTreeControl<ProjectFileFlatNode>;
  toggleTreeNode: BehaviorSubject<ProjectFileFlatNode> = new BehaviorSubject(null);

  constructor(private projectFilesChecklistService: ProjectFilesChecklistService) {
  }

  initialize(projectFiles: ProjectFile[]): ProjectFileNode[] {
    return this.generateNodes(projectFiles);
  }

  getNode(key: string): ProjectFileNode {
    return this.nodeMap.get(key);
  }

  removeNode(key: string): ProjectFileNode {
    const removedNode = this.nodeMap.get(key);
    this.nodeMap.delete(key);
    this.manuallyEditedTreeData.next({ action: ProjectFileUpdateAction.Remove, filePath: key });
    return removedNode;
  }

  addNode(key: string, node: ProjectFileNode) {
    this.nodeMap.set(key, node);
    this.manuallyEditedTreeData.next({ action: ProjectFileUpdateAction.Add, filePath: key, addNode: node });
  }

  setTree(processedNodes: ProjectFileNode[], pathFilters: string[], ignoreInitialFilters: boolean) {
    this.dataChange.next(processedNodes);
    processedNodes.forEach(node => this.projectFilesChecklistService.setSelection(node, pathFilters, ignoreInitialFilters));
  }

  setTreeSelection(pathFilters: string[]) {
    this.nodeMap.forEach(node => this.projectFilesChecklistService.setSelection(node, pathFilters ? pathFilters : []));
  }

  private generateNodes(projectFiles: ProjectFile[]): ProjectFileNode[] {
    const nodes: ProjectFileNode[] = [];
    projectFiles?.forEach(file => {
      const existingNode = this.nodeMap.get(file.Path);
      if (existingNode) {
        nodes.push(existingNode);
      } else {
        const node = new ProjectFileNode(file.Name, file.Id, file.Path, null, null, file.Type === ProjectFileSystemType.Folder, file.IsCollabFile, file.IsUncommitted);
        this.nodeMap.set(file.Path, node);
        nodes.push(node);
      }
    });

    return nodes;
  }

  getChildren$(path: string, branchName: string): Observable<ProjectFileNode[]> {
    return this.dataProvider.getTree$({
      projectId: this.projectId,
      branchName,
      treePath: path,
      includeFiles: this.includeFiles,
      fileFilter: this.fileFilter,
      committedFilter: this.committedFilter
    }).pipe(
      catchError((err) => {
        const flatNode = this.getNode(path)?.flatFileNode;
        if (flatNode) {
          flatNode.loaded = err.status === 404;
          flatNode.isLoading = false;
          flatNode.error = true;
          this.toggleTreeNode.next(flatNode);
        }
        return of(null);
      }),
      map(projectFiles => projectFiles?.sort((a, b) => {
        if (a.Type !== b.Type) {
          return a.Type === ProjectFileSystemType.Folder ? -1 : 1;
        }
        return a.Path.localeCompare(b.Path);
      })),
      map(projectFiles => projectFiles ? this.generateNodes(projectFiles) : null)
    );
  }

  addChildrenToFolderNode(node: ProjectFileNode, nodes: ProjectFileNode[], pathFilters: string[], ignoreInitialFilters: boolean) {
    node.flatFileNode.error = false;
    node.flatFileNode.isLoading = false;
    node.flatFileNode.loaded = true;
    node.childrenChange.next(nodes);
    this.dataChange.next(this.dataChange.value);
    if (!ignoreInitialFilters) {
      nodes.forEach(childNode => { childNode.parentNode = node; this.projectFilesChecklistService.setSelection(childNode, pathFilters); });
    }
  }

  addNewFileNodeToTree(node: ProjectFileFlatNode, newProjectFileNode: ProjectFileNode) {
    const parent = this.getNode(node.path);
    parent.children.push(newProjectFileNode);
    parent.childrenChange.next(parent.children);
    this.dataChange.next(this.dataChange.value);
  }
}
